import { FC, useContext, useState } from 'react';
import { SMBSelect, Card } from '../../../components';
import { useQuery } from 'react-query';
import { useSnackbar } from 'notistack';
import { IDropdownResponse } from '../../../models';
import { getFinancingTypes, getLenders } from '../../../fetch/lookups/sales-portal';
import { Grid, MenuItem, Button } from '@mui/material';
import { AccountBalance, Save } from '@mui/icons-material';
import { saveLenderInfo } from '../../../fetch';
import { UserContext } from '../../../context/user';
import { useFormikContext } from 'formik';

type IFinancingInformation = {
  buildId: number;
  neighborhoodId: number;
  financingType: string;
  lenderId: number | string;
  cardContentRootClassName?: string;
  fetchOptionDetails?: () => Promise<void>;
};

export const FinancingInformation: FC<IFinancingInformation> = ({
  buildId,
  neighborhoodId,
  financingType,
  lenderId,
  cardContentRootClassName,
  fetchOptionDetails
}) => {
  const { user } = useContext(UserContext);
  const { enqueueSnackbar } = useSnackbar();
  const isClient = user?.userRoles?.includes('BL_Client');
  const [isSaving, setSaving] = useState(false);

  const { isLoading: isLoadingFinancingTypes, data: financingTypes } = useQuery<
    IDropdownResponse[],
    Error
  >(['financing-types'], async () => await getFinancingTypes(), {
    notifyOnChangeProps: 'tracked'
  });
  const { isLoading: isLoadingLenders, data: lenders } = useQuery<IDropdownResponse[], Error>(
    ['lenders'],
    async () => await getLenders(neighborhoodId),
    {
      notifyOnChangeProps: 'tracked'
    }
  );

  const showLender = () => {
    if (financingType !== 'Cash') {
      return true;
    } else {
      return false;
    }
  };

  const { touched, isSubmitting, errors, setFieldValue } = useFormikContext<{
    financingType: string;
    lenderId: string;
  }>();

  return (
    <Card
      cardContentRootClassName={cardContentRootClassName}
      icon={<AccountBalance />}
      title="Financing Information"
    >
      <Grid container spacing={1}>
        {!isClient && (
          <>
            <Grid display={'none'} displayPrint={'block'} item xs={6}>
              <div>Financing Information:</div>
              {financingTypes?.find(type => type?.value === financingType)?.description ?? ''}
            </Grid>
            <Grid displayPrint={'none'} item xs={12} lg={6}>
              <SMBSelect
                label="Financing Information"
                disabled={isLoadingFinancingTypes || isSaving}
                value={financingType} // Add the correct type assertion
                required
                onChange={({ target: { value } }) => {
                  setFieldValue('financingType', value);
                  if (value === 'Cash') {
                    setFieldValue('lenderId', '');
                  }
                }}
                error={Boolean(touched?.financingType && errors.financingType)}
                helperText={touched?.financingType && errors.financingType}
              >
                {financingTypes?.map((type, index) => {
                  return (
                    <MenuItem key={`${type.value}`} value={type.value}>
                      {type.description}
                    </MenuItem>
                  );
                })}
              </SMBSelect>
            </Grid>
          </>
        )}
        {showLender() && (
          <>
            <Grid display={'none'} displayPrint={'block'} item xs={6}>
              <div>Lender:</div>
              {lenders?.find(lender => +lender?.value === +lenderId)?.description ?? ''}
            </Grid>
            <Grid displayPrint={'none'} item xs={12} lg={6}>
              <SMBSelect
                label="Lender"
                disabled={isLoadingLenders || isSaving}
                value={lenderId}
                required
                onChange={({ target: { value } }) => setFieldValue('lenderId', value)}
                error={Boolean(touched.lenderId && errors.lenderId)}
                helperText={touched.lenderId && errors.lenderId}
              >
                {lenders?.map((lender, index) => {
                  return (
                    <MenuItem key={`${lender.value}`} value={lender.value}>
                      {lender.description}
                    </MenuItem>
                  );
                })}
              </SMBSelect>
            </Grid>
          </>
        )}
        {!isClient && (
          <Grid displayPrint={'none'} item xs={12}>
            <Button
              type="button"
              startIcon={<Save />}
              variant="contained"
              color="primary"
              disabled={isSaving || isSubmitting || !financingType || (showLender() && !lenderId)}
              onClick={async () => {
                try {
                  setSaving(true);
                  await saveLenderInfo(buildId, {
                    financingType,
                    lenderId
                  });
                  await fetchOptionDetails?.();
                  enqueueSnackbar(`Saved Lender Info!`, {
                    variant: 'success'
                  });
                } catch (error) {
                  enqueueSnackbar(error?.Detail ?? `Error saving lender info, please try again.`, {
                    variant: 'error'
                  });
                } finally {
                  setSaving(false);
                }
              }}
            >
              Save Lender Info
            </Button>
          </Grid>
        )}
      </Grid>
    </Card>
  );
};
